import { type FieldValues, useController } from 'react-hook-form';
import {
  AutocompleteField,
  type AutocompleteFieldRecordProps,
  type AutocompleteFieldStringProps,
} from '../AutocompleteField/AutocompleteField';
import type {
  FormControlProps,
  TypedUseControllerProps,
} from '../FormControl/FormControl.types';
import { useStableCallback } from '../hooks/useStableCallback';

export type FormAutocompleteRecordProps<
  TFieldValues extends FieldValues = FieldValues,
  TOption extends Record<string, any> = Record<string, any>,
> = TypedUseControllerProps<TFieldValues, string> &
  FormControlProps<AutocompleteFieldRecordProps<TOption>> & {
    // we want expose onChange to allow parent to react to changes
    onChange?: (value: string) => void;
  };

export type FormAutocompleteStringProps<
  TFieldValues extends FieldValues = FieldValues,
> = TypedUseControllerProps<TFieldValues, string> &
  FormControlProps<AutocompleteFieldStringProps> & {
    // we want expose onChange to allow parent to react to changes
    onChange?: (value: string) => void;
  };

const FormAutocompleteRecord = <
  TFieldValues extends FieldValues = FieldValues,
  TOption extends Record<string, any> = Record<string, any>,
>({
  disabled,
  onChange,
  onClear,
  optionValueName,
  isOptionEqualToValue = (option, value) => option[optionValueName] === value,
  getOptionLabel = (option) => option[optionValueName],
  ...props
}: FormAutocompleteRecordProps<TFieldValues, TOption>) => {
  const {
    field: { ref, onChange: onFieldChange, ...field },
    formState: { errors },
  } = useController(props);

  const error = errors[props.name]?.message?.toString();

  const handleChange = useStableCallback((value: string) => {
    onFieldChange(value);
    onChange?.(value);
  });

  return (
    <AutocompleteField.Record
      {...props}
      {...field}
      disabled={disabled}
      error={error}
      getOptionLabel={getOptionLabel}
      inputRef={ref}
      isOptionEqualToValue={isOptionEqualToValue}
      onChange={handleChange}
      onClear={() => onClear?.() || handleChange('')}
      optionValueName={optionValueName}
    />
  );
};

const FormAutocompleteString = <
  TFieldValues extends FieldValues = FieldValues,
>({
  disabled,
  onChange,
  onClear,
  ...props
}: FormAutocompleteStringProps<TFieldValues>) => {
  const {
    field: { ref, onChange: onFieldChange, ...field },
    formState: { errors },
  } = useController(props);

  const error = errors[props.name]?.message?.toString();

  const handleChange = useStableCallback((value: string) => {
    onFieldChange(value);
    onChange?.(value);
  });

  return (
    <AutocompleteField.String
      {...props}
      {...field}
      disabled={disabled}
      error={error}
      inputRef={ref}
      onChange={handleChange}
      onClear={() => onClear?.() || handleChange('')}
    />
  );
};

export const FormAutocomplete = {
  String: FormAutocompleteString,
  Record: FormAutocompleteRecord,
};
